import React from 'react';
import { Brain, Target, TrendingUp, Zap, CheckCircle, AlertCircle, DollarSign, BarChart3, Star, Users, Award, Lightbulb, Shield, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';

const FrameworksPage = () => {
  const navigate = useNavigate();
  const storytellingFrameworks = [
    {
      name: "Story Arc",
      icon: "📖",
      description: "The classic narrative structure that builds emotional connection through tension and resolution.",
      stages: ["Hook", "Problem", "Tension", "Climax", "Resolution"],
      bestFor: "Building brand affinity and emotional engagement",
      useCase: "Perfect for brand storytelling campaigns that aim to create lasting impressions"
    },
    {
      name: "3-Act Structure",
      icon: "🎬",
      description: "A time-tested framework from Hollywood that creates compelling narratives.",
      stages: ["Setup", "Confrontation", "Resolution"],
      bestFor: "Creating dramatic tension and satisfying conclusions",
      useCase: "Ideal for product launches and transformation stories"
    },
    {
      name: "Hero's Journey",
      icon: "⚔️",
      description: "Position your customer as the hero transforming through your product or service.",
      stages: ["Hero", "Call to Adventure", "Mentor", "Transformation", "Return with Reward"],
      bestFor: "Aspirational brands and life-changing products",
      useCase: "Best for fitness, education, and personal development niches"
    },
    {
      name: "Hook-Relate-Resolution (HRR)",
      icon: "🎣",
      description: "A modern, social-first framework designed for short-form video content.",
      stages: ["Hook", "Relate", "Resolution"],
      bestFor: "Quick engagement and relatability",
      useCase: "Perfect for TikTok, Instagram Reels, and YouTube Shorts"
    }
  ];

  const conversionFrameworks = [
    {
      name: "AIDA",
      icon: "🎯",
      description: "The foundational framework for driving action through a clear persuasion path.",
      stages: ["Attention", "Interest", "Desire", "Action"],
      bestFor: "Direct response campaigns and e-commerce",
      useCase: "Proven for product demos and limited-time offers"
    },
    {
      name: "PAS (Problem-Agitate-Solution)",
      icon: "⚡",
      description: "Amplify pain points before presenting your solution for maximum impact.",
      stages: ["Problem", "Agitate", "Solution"],
      bestFor: "Problem-solving products and B2B services",
      useCase: "Highly effective for SaaS and service-based businesses"
    },
    {
      name: "BAB (Before-After-Bridge)",
      icon: "🌉",
      description: "Paint a vivid contrast between the current state and desired outcome.",
      stages: ["Before", "After", "Bridge"],
      bestFor: "Transformation-based products",
      useCase: "Excellent for weight loss, productivity tools, and makeovers"
    }
  ];

  const statistics = [
    { value: "63%", label: "of ad budgets are wasted on poor creative", icon: AlertCircle, color: "text-red-500" },
    { value: "70%", label: "of campaign success is determined by creative quality", icon: DollarSign, color: "text-orange-500" },
    { value: "5x", label: "higher ROI with pre-tested video content", icon: TrendingUp, color: "text-green-500" },
    { value: "80%", label: "of users recall video ads that tell a story", icon: Brain, color: "text-purple-500" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center">
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <Star className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">Industry-Leading Marketing Frameworks</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Stop Wasting Money on
              <span className="block mt-2 bg-clip-text text-transparent bg-gradient-to-r from-yellow-200 to-orange-200">
                Unqualified Video Ads
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-indigo-100 max-w-3xl mx-auto">
              Qualify your videos before advertising. Save your media spend. Maximize ROI. Elevate creative production with proven marketing frameworks.
            </p>
          </div>
        </div>
      </section>

      {/* Why Analyze Before Advertising */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Analyze Videos Before Advertising?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every dollar spent on advertising a poorly performing video is a dollar wasted. Here's why pre-qualification matters.
            </p>
          </div>

          {/* Statistics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {statistics.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-200">
                  <Icon className={`w-10 h-10 ${stat.color} mb-4`} />
                  <div className={`text-4xl font-bold ${stat.color} mb-2`}>{stat.value}</div>
                  <p className="text-gray-600 text-sm">{stat.label}</p>
                </div>
              );
            })}
          </div>

          {/* Key Benefits with Quote */}
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-8 md:p-12 mb-12">
            <div className="flex items-start mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0 mr-6">
                <Lightbulb className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">The Cost of Skipping Analysis</h3>
                <blockquote className="text-lg text-gray-700 italic border-l-4 border-indigo-500 pl-6 mb-6">
                  "The most expensive video ad isn't the one that costs the most to produce—it's the one that fails to convert after you've spent thousands on distribution."
                </blockquote>
                <p className="text-gray-600 mb-4">
                  Professional marketers and agencies understand a fundamental truth: <strong>creative quality determines 70% of campaign performance</strong>. Yet most brands skip the crucial step of systematically analyzing their video content before putting media spend behind it.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                  Without Pre-Analysis
                </h4>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2 flex-shrink-0">✗</span>
                    <span className="text-gray-600">Blind spending on untested creative</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2 flex-shrink-0">✗</span>
                    <span className="text-gray-600">No data-driven optimization before launch</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2 flex-shrink-0">✗</span>
                    <span className="text-gray-600">Wasted budget on poor-performing hooks</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2 flex-shrink-0">✗</span>
                    <span className="text-gray-600">Missed opportunities for improvement</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2 flex-shrink-0">✗</span>
                    <span className="text-gray-600">Low confidence in campaign outcomes</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                  With Framework Analysis
                </h4>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2 flex-shrink-0">✓</span>
                    <span className="text-gray-600">Data-backed decisions before spending</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2 flex-shrink-0">✓</span>
                    <span className="text-gray-600">Identify and fix weak points in advance</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2 flex-shrink-0">✓</span>
                    <span className="text-gray-600">Optimize hooks, messaging, and CTAs</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2 flex-shrink-0">✓</span>
                    <span className="text-gray-600">Higher conversion rates from day one</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2 flex-shrink-0">✓</span>
                    <span className="text-gray-600">Predictable, scalable campaign performance</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Who Benefits */}
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-200">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center mb-4">
                <Award className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-bold text-gray-900 mb-3">Brands</h4>
              <p className="text-gray-600 text-sm mb-4">
                Protect your brand reputation and maximize ROAS by ensuring every video ad meets proven marketing standards before launch.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Reduce creative testing costs</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Launch with confidence</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Improve campaign consistency</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-200">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-bold text-gray-900 mb-3">Agencies</h4>
              <p className="text-gray-600 text-sm mb-4">
                Deliver superior results for clients with systematic video analysis. Prove your value with data-driven insights.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Win more client pitches</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Justify premium pricing</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Scale campaign success</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-200">
              <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-bold text-gray-900 mb-3">Creators</h4>
              <p className="text-gray-600 text-sm mb-4">
                Get clear direction on creating impactful video ads. Learn the frameworks that attract brands and secure long-term partnerships.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Get specific guidance on what makes videos impactful</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Create content brands want to work with</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Build ongoing brand partnerships</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Storytelling Frameworks */}
      <section id="frameworks-detail" className="py-16 md:py-20 bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-full px-4 py-2 mb-4">
              <Brain className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">Storytelling & Brand Building</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Narrative Frameworks That Build Emotional Connections
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transform viewers into loyal customers through the power of story. These frameworks help you create memorable brand experiences that drive long-term value.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {storytellingFrameworks.map((framework, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-200 border border-indigo-100">
                <div className="flex items-center mb-6">
                  <div className="text-4xl mr-4">{framework.icon}</div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{framework.name}</h3>
                    <p className="text-sm text-indigo-600 font-medium">{framework.bestFor}</p>
                  </div>
                </div>
                <p className="text-gray-600 mb-6">{framework.description}</p>
                
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Framework Stages:</h4>
                  <div className="flex flex-wrap gap-2">
                    {framework.stages.map((stage, idx) => (
                      <span key={idx} className="bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 px-3 py-1 rounded-full text-sm font-medium">
                        {stage}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-100">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-indigo-600">Use Case:</span> {framework.useCase}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-white">
            <div className="flex items-start">
              <Shield className="w-12 h-12 mr-6 flex-shrink-0" />
              <div>
                <h3 className="text-2xl font-bold mb-4">Why Storytelling Frameworks Matter</h3>
                <p className="text-indigo-100 mb-4">
                  Stories aren't just entertaining—they're scientifically proven to increase message retention by up to 22x compared to facts alone. When you structure your video ads using proven narrative frameworks, you tap into psychological triggers that have captivated audiences for thousands of years.
                </p>
                <ul className="space-y-2 text-indigo-100">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Emotional resonance:</strong> Stories trigger dopamine release, creating memorable experiences</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Brand differentiation:</strong> Stand out in crowded markets with compelling narratives</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Long-term value:</strong> Build brand affinity that transcends individual campaigns</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conversion Frameworks */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full px-4 py-2 mb-4">
              <Zap className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">Conversion & ROI Frameworks</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Persuasion Formulas That Drive Measurable Results
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              When you need immediate action and trackable conversions, these battle-tested frameworks deliver. Perfect for direct response campaigns and performance marketing.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {conversionFrameworks.map((framework, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-200 border border-orange-100">
                <div className="text-5xl mb-4">{framework.icon}</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{framework.name}</h3>
                <p className="text-sm text-orange-600 font-medium mb-4">{framework.bestFor}</p>
                <p className="text-gray-600 mb-6">{framework.description}</p>
                
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Framework Stages:</h4>
                  <div className="space-y-2">
                    {framework.stages.map((stage, idx) => (
                      <div key={idx} className="flex items-center">
                        <div className="w-6 h-6 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3 flex-shrink-0">
                          {idx + 1}
                        </div>
                        <span className="text-gray-700 font-medium">{stage}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-orange-600">Use Case:</span> {framework.useCase}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-8 border border-orange-200">
            <div className="flex items-start">
              <BarChart3 className="w-12 h-12 text-orange-500 mr-6 flex-shrink-0" />
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">The ROI Multiplier Effect</h3>
                <p className="text-gray-700 mb-4">
                  Conversion frameworks aren't just theory—they're proven formulas that have generated billions in revenue across every industry. When properly applied to video advertising, they create a systematic approach to persuasion that consistently outperforms ad-hoc creative approaches.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Key Benefits:</h4>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start">
                        <span className="text-orange-500 mr-2">•</span>
                        <span>Predictable conversion paths that reduce drop-off</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-orange-500 mr-2">•</span>
                        <span>Clear CTAs that drive immediate action</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-orange-500 mr-2">•</span>
                        <span>Optimized message hierarchy for better retention</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-orange-500 mr-2">•</span>
                        <span>Scalable templates for consistent performance</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">When to Use:</h4>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start">
                        <span className="text-orange-500 mr-2">•</span>
                        <span>Product launches and promotions</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-orange-500 mr-2">•</span>
                        <span>Limited-time offers and flash sales</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-orange-500 mr-2">•</span>
                        <span>Lead generation campaigns</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-orange-500 mr-2">•</span>
                        <span>Any campaign with clear conversion goals</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Analysis Process */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-indigo-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How Framework Analysis Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our AI evaluates your video against all seven frameworks simultaneously, giving you a comprehensive view of your content's strengths and optimization opportunities.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 mb-12">
            <div className="bg-white rounded-xl p-6 text-center shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl font-bold">1</span>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Upload Video</h3>
              <p className="text-sm text-gray-600">Submit your video ad for instant AI analysis</p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl font-bold">2</span>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">AI Analysis</h3>
              <p className="text-sm text-gray-600">Gemini AI evaluates against all frameworks</p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl font-bold">3</span>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Get Insights</h3>
              <p className="text-sm text-gray-600">Receive detailed scores and recommendations</p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl font-bold">4</span>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Optimize & Launch</h3>
              <p className="text-sm text-gray-600">Improve and deploy with confidence</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">What You'll Receive:</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <BarChart3 className="w-5 h-5 text-indigo-500 mr-2" />
                  Performance Scores
                </h4>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Overall effectiveness rating (0-100)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Framework-specific scores for each model</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Hook effectiveness (first 3 seconds)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Visual quality and engagement metrics</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Branding and CTA analysis</span>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <Lightbulb className="w-5 h-5 text-orange-500 mr-2" />
                  Actionable Recommendations
                </h4>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Specific improvements for each framework</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Hook optimization suggestions</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Message clarity and pacing tips</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>CTA strength and placement advice</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Platform-specific optimization tips</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Save Money and Increase ROI?
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Stop guessing. Start qualifying. Analyze your videos with proven marketing frameworks before spending a single dollar on advertising.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/')}
              className="bg-white text-indigo-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-indigo-50 transition-all duration-200 shadow-xl hover:shadow-2xl transform hover:scale-105"
            >
              Analyse Your Video Now
            </button>
          </div>
          <p className="text-sm text-indigo-200 mt-6">
            No credit card required • Instant results • Used by leading brands
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FrameworksPage;

